/**
 * Home health demo store — state shape.
 *
 * DEMO-ONLY. Everything here lives in browser memory (persisted to localStorage)
 * and models the home-health layer the product does not have yet: traits,
 * territories and ownership, holds and placeholders, readiness and auth,
 * episode plans, confirmation, countdown rows. Anything the product already
 * models (shifts, blocks, visits, providers) is read from the server into
 * `server` and never persisted; `local` is the optimistic overlay on top of it
 * for the clinician's own changes, also never persisted.
 *
 * Planning screens count in points (`config.visitTypes[kind].points`); hours stay
 * available for the KPI engine but are not the planning unit.
 *
 * Ids: `ProviderId`/`ServerId` are real server ids resolved at seed time and kept
 * in `meta.bindings`. `handle`/`ref` strings are stable fixture keys.
 */
import type { TimeSentence } from "./engine/timeText";

export type DateKey = string; // "YYYY-MM-DD" in the branch time zone
export type IsoString = string;
export type ProviderId = number;
export type ServerId = number;

export type Discipline = "RN" | "LPN" | "PT" | "PTA" | "OT" | "COTA" | "SLP" | "MSW" | "HHA";
/** Planning group a discipline rolls up to on the branch read (SN = RN + LPN, PT = PT + PTA, OT = OT + COTA). */
export type DisciplineGroup = "SN" | "PT" | "OT" | "SLP" | "MSW" | "HHA";
export type RoleKind = "assessing" | "assistant" | "msw";
export type Employment = "FT" | "PT" | "perDiem";
export type Payer = "MA" | "FFS";
export type GateKey = "orders" | "readinessCall" | "consent" | "auth";
export type GateStatus = "done" | "pending" | "needsAction";
export type PoolKind = "overflow" | "perDiemDeclared" | "perDiemUndeclared" | "dayOff";
export type ConfirmationChannel = "SMS" | "Voice partner";

export type VisitTypeKind =
  | "SOC"
  | "ROC"
  | "RECERT"
  | "DISCHARGE_ASSESSMENT"
  | "SN_ROUTINE"
  | "RN_SUPERVISORY"
  | "PT_EVAL"
  | "PT_ROUTINE"
  | "OT_EVAL"
  | "OT_ROUTINE"
  | "SLP_EVAL"
  | "SLP_ROUTINE"
  | "MSW_VISIT"
  | "HHA_VISIT"
  | "THERAPY_REASSESSMENT";

// ---------------------------------------------------------------------------
// meta
// ---------------------------------------------------------------------------

export interface ServerBindings {
  organizationId?: ServerId;
  serviceLineId?: ServerId;
  organizationalUnitId?: ServerId;
  providerIdsByHandle: Record<string, ProviderId>;
  visitTypeIdsByKind: Partial<Record<VisitTypeKind, ServerId>>;
  patientIdsByRef: Record<string, ServerId>;
  episodeIdsByRef: Record<string, ServerId>;
  shiftCenterIdsByName: Record<string, ServerId>;
  cancellationReasonIdsByName: Record<string, ServerId>;
}

export interface MetaState {
  schemaVersion: number;
  seedId: string;
  seededAt: IsoString;
  demoToday: DateKey;
  /** Days for which real (server) visits are authoritative; other days use the virtual census. */
  realDays: DateKey[];
  bindings: ServerBindings;
  /** True once the horizon read has resolved the tenant's ids into `bindings`. */
  bound: boolean;
  /**
   * Real visit ids that never produce a derived "Canceled, rebook" row: a cancel
   * the demo made as a correction (Patient not home). Persisted and carried
   * forward across RESET so a reset leaves no rows behind.
   */
  excludedVisitIds: number[];
  /**
   * Real visits the demo created (the SOC conversion, a rebook). They never raise
   * a "Canceled, rebook" row either; RESET folds them into `excludedVisitIds`.
   */
  createdVisitIds: number[];
  /**
   * The stamp of the tenant seed this store was built for (`engine/tenantDemoDay.ts`).
   * When the horizon reads a different one, the tenant has been reseeded and the
   * store rebuilds itself. Undefined for a store built before the tenant said.
   */
  tenantStamp?: string;
}

// ---------------------------------------------------------------------------
// configuration (the "agency configures…" surface)
// ---------------------------------------------------------------------------

export interface TerritoryConfig {
  name: string;
  zips: string[];
}

export interface BranchConfig {
  name: string;
  timeZone: string;
  serviceAreaZips: string[];
  closedZips: string[];
  territories: TerritoryConfig[];
}

export interface VisitTypeConfig {
  kind: VisitTypeKind;
  label: string;
  disciplines: Discipline[];
  plannedMinutes: number;
  points: number;
  requiredTraits: string[];
  isAssessment: boolean;
}

export interface FrequencyOrder {
  discipline: Discipline;
  /** EHR-style frequency string, e.g. "2w9", "1-3w9", "1w1", "2w3, 1q2w6" (every other week). */
  pattern: string;
  requiredTraits?: string[];
}

export interface InflowAssumption {
  /** Trailing average window the referral rate is taken from. */
  trailingWeeks: number;
  referralsPerWeekByDiscipline: Partial<Record<Discipline, number>>;
  /**
   * Points one active case consumes per week, by discipline. The field name is
   * kept from an earlier hours read so the persisted v2 store shape does not
   * move; the unit is points.
   */
  weeklyHoursPerCaseByDiscipline: Partial<Record<Discipline, number>>;
  /** Multiplier applied to SOC demand landing Fri/Mon. 1 = no skew. */
  friMonSkew: number;
  dischargesPerWeek: number;
  /** Points per week freed by one discharged case (field name kept, unit is points). */
  weeklyHoursPerDischargedCase: number;
}

export interface ConfigState {
  branch: BranchConfig;
  traitVocabulary: string[];
  visitTypes: Record<VisitTypeKind, VisitTypeConfig>;
  socSlot: { durationMinutes: number; driveMinutes: number };
  /** Readiness before conversion, per payer (internal; never rendered as gates). */
  gateSets: Record<Payer, GateKey[]>;
  certPeriodDays: number;
  paymentPeriodDays: number;
  lupaThresholdPerPeriod: number;
  starterFrequencies: Record<string, FrequencyOrder[]>;
  /** Display name per expected-plan template, keyed the same way as `starterFrequencies`. The key never renders. */
  planTemplateNames: Record<string, string>;
  /** Display of the engine's scoring configuration (C2). */
  scoreWeightsDisplay: { driveTime: number; continuity: number };
  /** The engine's hardcoded org-wide post-filter. Kept for the placement math; never shown on a planning screen. */
  maxDriveMinutes: number;
  candidatePoolOrder: PoolKind[];
  confirmationChannel: ConfirmationChannel;
  /** The hour the branch's confirmation round goes, "HH:mm" branch time (3.3). */
  confirmationSendTime: string;
  /** The hour after which an unanswered text is Needs a call, "HH:mm" branch time. */
  confirmationCutoffTime: string;
  inflow: InflowAssumption;
  /** Weekly points target and ceiling per employment type — the planning unit. */
  targetPointsByEmployment: Record<Employment, number>;
  ceilingPointsByEmployment: Record<Employment, number>;
  /** The morning documentation block every full-time clinician carries. */
  documentationBlock: { start: string; end: string };
}

// ---------------------------------------------------------------------------
// roster and patients
// ---------------------------------------------------------------------------

export interface RosterEntry {
  handle: string;
  firstName: string;
  lastName: string;
  /** "First Last" — what the product shows for the provider. */
  displayName: string;
  email: string;
  discipline: Discipline;
  roleKind: RoleKind;
  employment: Employment;
  traits: string[];
  gender?: "F" | "M";
  languages?: string[];
  targetHours: number;
  ceilingHours: number;
  targetPoints: number;
  ceilingPoints: number;
  rampWeek?: number;
  rampWeeks?: number;
  rampEndsOn?: DateKey;
  homeZip: string;
  /** Territories this clinician owns for their discipline: the default assignment and where their supply lands. Empty for per diem. */
  territories: string[];
  /** Territories this clinician covers when the owner is full (the contested-placement alternative). */
  coversTerritories?: string[];
  /** Primary owned territory (the first of `territories`, or the home-zip territory for per diem). Derived at seed. */
  territory: string;
  /** Per diem: days declared available beyond what the horizon's shifts already show. */
  declaredDays?: DateKey[];
  lastWorkedOn?: DateKey;
  lastContactOn?: DateKey;
  providerId?: ProviderId;
}

export interface PatientConstraint {
  kind: "availabilityWindow" | "availabilityDays" | "language" | "gender" | "caregiver" | "smsConsent";
  value: string;
  /** Who learned it — the welcome call, or the clinician who found out at the door. Absent means the welcome call. */
  addedBy?: string;
  /** When it was learned; the availability report prints it beside the name. */
  addedAtIso?: IsoString;
}

export interface DemoPatient {
  ref: string;
  displayName: string;
  /** Real names as seeded in the tenant; used to bind to the server patient. */
  firstName: string;
  lastName: string;
  /** The MRN the seed gave the patient; binds to the server patient ahead of the name. */
  mrn?: string;
  serverId?: ServerId;
  payer: Payer;
  zip: string;
  territory?: string;
  constraints: PatientConstraint[];
}

// ---------------------------------------------------------------------------
// referrals, holds, episodes, plan
// ---------------------------------------------------------------------------

/**
 * `declined` is the branch saying no to a referral it never accepted, which is
 * different from `released` — that is a hold given back after one was placed.
 */
export type ReferralStatus = "pending" | "new" | "accepted" | "released" | "declined";

/**
 * What the person deciding the referral chose for its expected plan. Absent
 * means nobody has chosen: the plan is the orders if they carry frequencies,
 * otherwise the template suggested from the disciplines and the case type.
 */
export type ReferralPlanChoice =
  | { kind: "template"; templateKey: string }
  | { kind: "custom"; orders: FrequencyOrder[]; horizonWeeks: number }
  | { kind: "ordersOnFile" };

export interface Referral {
  id: string;
  patientRef: string;
  source: string;
  receivedAtIso: IsoString;
  orders: FrequencyOrder[];
  caseType: string;
  /** `pending` = intake has it but it has not arrived on the queue yet (the Referral arrives event flips it to `new`). */
  status: ReferralStatus;
  expectedDischarge: DateKey;
  /** Set when intake reports a changed discharge date; cleared by the re-hold. */
  dischargeChangedAtIso?: IsoString;
  /** The expected plan the branch chose. Absent = the suggestion stands. */
  expectedPlan?: ReferralPlanChoice;
}

export interface TailReservation {
  discipline: Discipline;
  requiredTraits: string[];
  hoursPerWeek: number;
  pointsPerWeek: number;
  weeks: number;
}

export type HoldStatus = "held" | "converted" | "released";

export interface Hold {
  id: string;
  referralId: string;
  patientRef: string;
  providerHandle?: string;
  /**
   * **Who intake put this on**, which `providerHandle` forgets the moment a hold
   * loses its hour. A hold whose span is taken becomes *Needs a time* — it keeps
   * the person and gives up the clock — and the search that looks for it a new
   * hour looks on **their** day only. Without this the search walked the whole
   * roster and a hold authored on Marcus settled on Dana, taking a morning the
   * seed keeps for something else (2026-09-24).
   *
   * Absent on a hold nobody assigned, which is genuinely free to go to whoever
   * has room.
   */
  authoredHandle?: string;
  date: DateKey;
  windowStart: string; // "HH:mm"
  windowEnd: string;
  status: HoldStatus;
  tail: TailReservation[];
  convertedVisitId?: ServerId;
}

/** Readiness before conversion, keyed by hold id. Internal: decides FFS-on-welcome-call / MA-on-auth; never rendered as a strip. */
export type HoldReadiness = Partial<Record<GateKey, GateStatus>>;

export interface AuthLine {
  discipline: Discipline;
  unitsAuthorized: number;
  unitsUsed: number;
  fromDate: DateKey;
  toDate: DateKey;
}

export interface PlanVersion {
  version: number;
  createdAtIso: IsoString;
  reason: string;
  orders: FrequencyOrder[];
}

/** A discipline that was ordered (eval) but whose frequencies have not landed: an estimate on the branch read, not a calendar object. */
export interface PendingDiscipline {
  discipline: Discipline;
  pointsPerWeek: number;
  weeks: number;
}

export interface Episode {
  id: string;
  patientRef: string;
  socDate: DateKey;
  certEndDate: DateKey;
  versions: PlanVersion[];
  authLedger: AuthLine[];
  lupaThreshold: number;
  pendingDisciplines: PendingDiscipline[];
  /**
   * A generated census patient's episode: it gives their cards a plan, a period
   * and a week, but raises no worklist rows — the worklist is the script's.
   */
  background?: boolean;
}

export type PlanVisitStatus = "placeholder" | "real" | "completed" | "missed" | "canceled";

export interface PlanVisit {
  id: string;
  episodeId: string;
  discipline: Discipline;
  visitTypeKind: VisitTypeKind;
  weekIndex: number;
  targetDate: DateKey;
  windowStart?: string;
  windowEnd?: string;
  providerHandle?: string;
  requiredTraits: string[];
  status: PlanVisitStatus;
  realVisitId?: ServerId;
  rule?: "supervisory" | "recert";
  planVersion: number;
  /** For a discipline whose orders arrived after the SOC: the date its track is anchored on. */
  anchorDate?: DateKey;
}

// ---------------------------------------------------------------------------
// annotations on real visits, worklist items, sandbox, presenter
// ---------------------------------------------------------------------------

export type ConfirmationStatus = "notSent" | "unconfirmed" | "confirmed" | "declined";

/**
 * The confirmation round (3.3). Nothing is drafted: at the time the branch sets,
 * every patient with a visit on the clinician's next working day and text
 * consent gets one message carrying the time the schedule already has, and the
 * visit reads `pending`. A yes confirms it. Everything else is `needsCall` and
 * carries its reasons on the row.
 *
 * **There is no `declined`** (Pete, 2026-09-24): *"I don't see how we could
 * differentiate that reliably with simple parsing of a single response message
 * which is all we have the capability to do."* Telling "No, tomorrow doesn't
 * work" from "can you come later?" is not keyword matching, and the screen
 * should not claim an understanding the parser has not got. So the round
 * recognises a yes — a closed set of whole messages, `isAffirmative` — and
 * hands everything else to a person with the patient's own words attached,
 * which is more use to whoever reads it than a label would have been.
 */
export type RoundStatus = "notSent" | "pending" | "confirmed" | "needsCall";

/**
 * Why a row needs a call. Reasons **accumulate**: a row holding the patient's
 * words keeps them and gains `moved`, and a visit moved twice carries `moved`
 * once (`addReason`, `engine/confirmationRound.ts`).
 */
export interface ConfirmationReason {
  kind: "words" | "noConsent" | "moved";
  /** `words` only: what the patient actually wrote, quoted on the row. */
  text?: string;
}

export interface VisitRound {
  status: RoundStatus;
  reasons: ConfirmationReason[];
  /** When the one text went out. Absent on a row that was never texted. */
  textedAtIso?: IsoString;
  /**
   * **What the patient was actually told**: the clinician, the start and the
   * arrival window as they stood at the moment the text went out. These never
   * move under a snapshot — there is never a second text — so *Moved after the
   * text* is measured against what was said rather than against wherever the
   * visit drifted to last. A **Confirm** re-anchors them, because the phone call
   * is a new telling.
   */
  textedProviderId?: ProviderId;
  textedStartIso?: IsoString;
  textedWindowStartIso?: IsoString;
  textedWindowEndIso?: IsoString;
  repliedAtIso?: IsoString;
  confirmedAtIso?: IsoString;
  /** The clinician confirmed it on the phone rather than the patient by text. */
  confirmedByPhone?: boolean;
}

/**
 * One round: one clinician, one day, keyed `${providerId}:${DateKey}`. The
 * clinician's only control over it is **Delay**, an hour at a time up to two,
 * for her own round and for today only.
 */
export interface ConfirmationRound {
  delayHours: number;
  /** Set when the round went. Before that every row on the day reads Not sent. */
  firedAtIso?: IsoString;
  /** Set when the cutoff passed and whoever never answered became Needs a call. */
  cutoffAtIso?: IsoString;
}

export interface VisitAnnotation {
  realVisitId: ServerId;
  planVisitId?: string;
  holdId?: string;
  origin: "census" | "soc" | "plan" | "rebook";
  confirmation: ConfirmationStatus;
  /** Where the visit stood when its text went out; anything else is a move (`movedAfterText`). */
  lastSeenProviderId?: ProviderId;
  lastSeenStartIso?: IsoString;
  /** Conversion snapped the SOC start inside the patient's window from the welcome call. */
  placedInsideWindow?: { holdStart: string; placedStart: string; window: string };
  /** The confirmation round (3.3): where this visit's one text has got to. */
  round?: VisitRound;
}

/** Store-created worklist rows. Everything else on the worklist is derived. No assignees, no claim, no resolve button. */
export type RowKind = "availabilityConflict" | "authDue";

export interface WorklistItem {
  id: string;
  kind: RowKind;
  createdAtIso: IsoString;
  patientRef?: string;
  episodeId?: string;
  dueAtIso?: IsoString;
  /**
   * The row's prose. Composed as parts — literal text with a time token where a
   * clock time belongs — so the view phrases the hour under the user's own
   * twelve/twenty-four-hour setting. A plain string is still a valid note, which
   * is what a store written before the sweep holds.
   */
  note: TimeSentence;
  resolved: boolean;
  /** Availability conflict only: the plan visit the row is about, so the row can be re-checked against the plan on every read. */
  planVisitId?: string;
}

/** A hypothetical case added in the sandbox: weekly hours landing in a territory for a few weeks. */
export interface SandboxCase {
  id: string;
  discipline: Discipline;
  territory: string;
  hoursPerWeek: number;
  weeks: number;
  /** Monday DateKey of the first week the case draws capacity. */
  startWeek: DateKey;
}

export interface SandboxState {
  removedHandles: string[];
  addedClinicians: RosterEntry[];
  inflow?: Partial<InflowAssumption>;
  zipToggles: Record<string, "open" | "closed">;
  addedCases?: SandboxCase[];
}

export interface VirtualDayOverride {
  /** SOC / ROC / recert / discharge assessments that day. */
  assessments?: number;
  routineVisits?: number;
  bookedMinutes?: number;
  driveMinutes?: number;
  points?: number;
}

export interface VirtualCensusState {
  seed: number;
  /** `${handle}:${DateKey}` → per-day override */
  overrides: Record<string, VirtualDayOverride>;
}

export interface PresenterLogLine {
  atIso: IsoString;
  level: "info" | "warn" | "error";
  text: string;
}

export interface PresenterState {
  fired: string[];
  log: PresenterLogLine[];
  /** The open call-out (4.1), with the shift it canceled so Restore can confirm it again. */
  callOut?: { providerHandle: string; date: DateKey; startedAtIso: IsoString; shiftId?: number };
}

// ---------------------------------------------------------------------------
// real data snapshot (never persisted)
// ---------------------------------------------------------------------------

export interface ShiftLike {
  id: ServerId;
  providerId: ProviderId;
  startIso: IsoString;
  endIso: IsoString;
  status: "confirmed" | "canceled";
  overflow: boolean;
  shiftCenterId?: ServerId;
  shiftCenterName?: string;
  shiftCenterZip?: string;
  providerZip?: string;
  useShiftCenterDriveTimes: boolean;
}

export interface BlockLike {
  id: ServerId;
  providerId: ProviderId;
  startIso: IsoString;
  endIso: IsoString;
  blockType: "break" | "meeting" | "facility" | "visit_type";
  visitTypeIds: ServerId[] | null;
  title?: string;
  address?: string;
  canceled: boolean;
}

export interface VisitResourceLike {
  id: ServerId;
  visitId: ServerId;
  providerId?: ProviderId;
  startIso: IsoString;
  endIso: IsoString;
  durationMinutes: number;
  startOfShiftDriveMinutes: number;
  interVisitDriveMinutes: number;
  endOfShiftDriveMinutes: number;
  visitTypeId: ServerId;
  visitTypeName: string;
  patientId: ServerId;
  patientName: string;
  patientZip?: string;
  eventStatus: string;
  arrivalWindowStartIso?: IsoString;
  arrivalWindowEndIso?: IsoString;
}

export interface ProviderLike {
  id: ProviderId;
  displayName: string;
  email?: string;
  providerTypeName?: string;
  homeZip?: string;
  active: boolean;
}

export interface PatientLike {
  id: ServerId;
  firstName: string;
  lastName: string;
  zip?: string;
  /** The patient's MRN identifier, when it has one. */
  mrn?: string;
}

export interface EpisodeLike {
  id: ServerId;
  patientId: ServerId;
  active: boolean;
}

export interface CancellationReasonLike {
  id: ServerId;
  description: string;
}

export interface ServerSnapshot {
  fetchedAtIso?: IsoString;
  rangeStart?: DateKey;
  rangeEnd?: DateKey;
  shifts: ShiftLike[];
  blocks: BlockLike[];
  visitResources: VisitResourceLike[];
  providers: ProviderLike[];
  patients: PatientLike[];
  episodes: EpisodeLike[];
  cancellationReasons: CancellationReasonLike[];
}

export const EMPTY_SERVER_SNAPSHOT: ServerSnapshot = {
  shifts: [],
  blocks: [],
  visitResources: [],
  providers: [],
  patients: [],
  episodes: [],
  cancellationReasons: [],
};

// ---------------------------------------------------------------------------
// optimistic overlay (never persisted)
// ---------------------------------------------------------------------------

/**
 * What the clinician just did on My week, applied before the server confirms it.
 * The reducer drops an entry when a snapshot satisfies it; entries older than
 * `LOCAL_OVERLAY_TTL_MS` are dropped with a presenter warning; a rejected mutation
 * rolls its entry back.
 */
export interface LocalVisitChange {
  visitId: ServerId;
  providerId?: ProviderId;
  startIso: IsoString;
  endIso: IsoString;
  canceled?: boolean;
  appliedAtIso: IsoString;
}

export interface LocalAddedVisit {
  tempId: string;
  providerId: ProviderId;
  patientId?: ServerId;
  patientName: string;
  visitTypeId?: ServerId;
  visitTypeName: string;
  startIso: IsoString;
  endIso: IsoString;
  appliedAtIso: IsoString;
}

export interface LocalAddedBlock {
  tempId: string;
  providerId: ProviderId;
  startIso: IsoString;
  endIso: IsoString;
  title: string;
  address?: string;
  appliedAtIso: IsoString;
}

/**
 * A block already on the server that she has just removed. Only the id is
 * needed: the card goes at click time and the entry clears when a snapshot
 * reads the block canceled — or no longer reads it at all.
 */
export interface LocalCanceledBlock {
  blockId: ServerId;
  appliedAtIso: IsoString;
}

/**
 * A block already on the server that she has just re-timed on My day. The block
 * moves on the axis at drop time and the entry clears when a snapshot reads it
 * at the new hour. Its provider never changes: a block is dragged within one
 * clinician's own day.
 */
export interface LocalMovedBlock {
  blockId: ServerId;
  startIso: IsoString;
  endIso: IsoString;
  appliedAtIso: IsoString;
}

export interface LocalOverlay {
  /** Keyed by real visit id (as a string). */
  visits: Record<string, LocalVisitChange>;
  addedVisits: LocalAddedVisit[];
  blocks: LocalAddedBlock[];
  /** Keyed by server block id (as a string). */
  canceledBlocks: Record<string, LocalCanceledBlock>;
  /** Keyed by server block id (as a string). */
  movedBlocks: Record<string, LocalMovedBlock>;
}

export const EMPTY_LOCAL_OVERLAY: LocalOverlay = { visits: {}, addedVisits: [], blocks: [], canceledBlocks: {}, movedBlocks: {} };

export const LOCAL_OVERLAY_TTL_MS = 60_000;

// ---------------------------------------------------------------------------
// root
// ---------------------------------------------------------------------------

export interface DemoState {
  meta: MetaState;
  config: ConfigState;
  roster: Record<string, RosterEntry>;
  patients: Record<string, DemoPatient>;
  referrals: Record<string, Referral>;
  holds: Record<string, Hold>;
  readiness: Record<string, HoldReadiness>;
  episodes: Record<string, Episode>;
  planVisits: Record<string, PlanVisit>;
  annotations: Record<string, VisitAnnotation>;
  /** The confirmation round per clinician per day (3.3), keyed `${providerId}:${DateKey}`. */
  confirmationRounds: Record<string, ConfirmationRound>;
  virtualCensus: VirtualCensusState;
  sandbox: SandboxState | null;
  worklistItems: WorklistItem[];
  /**
   * Address lines of blocks this demo created, keyed `id:<blockId>`. The horizon
   * read does not carry a block's address, so the card takes it from here.
   */
  blockAddresses: Record<string, string>;
  presenter: PresenterState;
  /** Real data. Not persisted; refreshed by the horizon fetcher. */
  server: ServerSnapshot;
  /** Optimistic changes on top of `server`. Not persisted; reconciled on every snapshot. */
  local: LocalOverlay;
}

/**
 * Bump when persisted shape changes incompatibly; a mismatch re-seeds.
 * v2 = points, ownership, rows, overlay.
 * v3 = the expected plan: `config.planTemplateNames`, `Referral.expectedPlan`,
 *      and referrals that carry the disciplines intake sent rather than the
 *      starter frequencies the store used to bake in. A v2 envelope carries
 *      neither, so it re-seeds instead of showing a referral with an HCHB-style
 *      frequency string and a dropdown that reads Orders on file.
 * v4 = the confirmation round: `annotations[].round` in place of the drafted
 *      window, `confirmationRounds`, and the branch's send time and cutoff. A v3
 *      envelope carries a drafted window with no round behind it, so it re-seeds
 *      rather than showing a row in a state the panel no longer has.
 * v5 = the state moved onto the visit card as an icon and the Tomorrow's times
 *      panel went: `VisitRound` gains the anchors the text actually carried
 *      (`textedProviderId`, `textedStartIso`, `textedWindow*`), without which a
 *      v4 round would treat every re-time as a move.
 *  6 — plan visits are dated where the seed placed them (`store/scheduledDays.ts`),
 *      every admitted patient carries an episode (`background` for the generated
 *      census), and `meta.tenantStamp` names the seed the store was built for. A
 *      v5 store holds the old plan dates and no stamp; it rebuilds rather than
 *      reading unbound until the next reseed.
 *  7 — patients carry the seed's MRN and bind by it. A v6 store has none and
 *      would bind by name only, so it rebuilds.
 *  8 — the journal is gone (undo is Reset demo): `meta.createdVisitIds` and
 *      `blockAddresses` keep the two things other screens read from it, and the
 *      call-out carries its shift id.
 *  9 — the 30-minute script (Phase B): the beats it cut take their state with
 *      them — the contested-placement row and its options, the interim overlay,
 *      the hospital transfer (`transferredAtIso`, the suspended status, the ROC
 *      rule), the not-home slide's round state. A v8 store may hold any of
 *      them, so it rebuilds.
 */
export const DEMO_SCHEMA_VERSION = 9;
